import { frequencyMeta } from './ssPeriodLine';

/**
 * Compact SS + term labels for Inspections summary:
 *   ss 1.0(Y-Y), ss 2.0(M-Y), ss 3.1(Q-Y-front)
 * Detail titles still use: Building · ss 1.0(M-10) …
 * Term index is relative to CS/BWOF anniversary.
 */

function parseYmd(value) {
  if (!value) return null;
  const s = String(value).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return { y: Number(m[1]), mo: Number(m[2]), d: Number(m[3]) };
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return { y: d.getFullYear(), mo: d.getMonth() + 1, d: d.getDate() };
}

function toDate({ y, mo, d }) {
  return new Date(y, mo - 1, d);
}

function cycleStart(anniversary, due) {
  const a = parseYmd(anniversary);
  const u = parseYmd(due);
  if (!a || !u) return null;
  let start = new Date(u.y, a.mo - 1, a.d);
  if (start > toDate(u)) start = new Date(u.y - 1, a.mo - 1, a.d);
  return start;
}

function monthDiff(from, to) {
  return (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
}

function normalizeFrequency(freq) {
  return String(freq || 'ANNUAL')
    .toUpperCase()
    .replace(/-/g, '_');
}

function sanitizeLoc(loc) {
  const raw = String(loc || '').trim();
  if (!raw) return '';
  // Performance-standard / CS prose must never appear in chips (e.g. "or SS 3/2. Inspection…")
  if (raw.length > 28) return '';
  if (/\bshall\b/i.test(raw)) return '';
  if (/\binpection\b/i.test(raw)) return '';
  if (/\bor\s+ss\b/i.test(raw)) return '';
  if (/[.…]/.test(raw) && raw.split(/\s+/).length > 3) return '';
  return raw
    .replace(/\s+/g, '-')
    .replace(/[()]/g, '')
    .slice(0, 24);
}

/**
 * @returns {{ letter: string, token: string } | null}
 * Annual → Y-Y; season last → M-Y / Q-Y / H-Y; else M-n / Q-n / H-n
 */
export function inspectionTermToken(frequency, dueDate, anniversary) {
  const f = normalizeFrequency(frequency);
  let letter = 'Y';
  let periodMonths = 12;
  if (f.includes('DAILY') && !f.includes('MONTHLY')) {
    return { letter: 'D', token: 'D' };
  }
  if (f.includes('SIX_MONTHLY') || f === 'BIANNUAL') {
    letter = 'H';
    periodMonths = 6;
  } else if (f.includes('QUARTER') || f === 'THREE_MONTHLY') {
    letter = 'Q';
    periodMonths = 3;
  } else if (f === 'MONTHLY' || (f.includes('MONTHLY') && !f.includes('ANNUAL') && !f.includes('SIX'))) {
    letter = 'M';
    periodMonths = 1;
  } else {
    return { letter: 'Y', token: 'Y-Y' };
  }

  const start = cycleStart(anniversary, dueDate);
  const due = parseYmd(dueDate);
  if (!start || !due) {
    return { letter, token: letter === 'Y' ? 'Y-Y' : letter };
  }

  const max = Math.floor(12 / periodMonths);
  let idx = Math.floor(monthDiff(start, toDate(due)) / periodMonths) + 1;
  if (idx < 1) idx = 1;
  if (idx > max) idx = max;

  if (idx === max) {
    return { letter, token: `${letter}-Y` };
  }
  return { letter, token: `${letter}-${idx}` };
}

/** Nav title: ss 9(Quarterly). The word is the same period value as the SS line. */
export function formatSsFrequencyChip(row) {
  const ss = formatSsCode(row?.ss_code);
  const period = frequencyMeta(row?.ss_frequency || row?.inspection_frequency).label;
  return `${ss}(${period})`;
}

/** Compact code: "ss 1.0" */
export function formatSsCode(ssCode) {
  if (ssCode == null || String(ssCode).trim() === '') return 'ss';
  const s = String(ssCode).trim().replace(/^ss\s*/i, '');
  return `ss ${s}`;
}

/**
 * One SS chip: ss 1.0(Y-Y) or ss 3.1(Q-Y-front)
 */
export function formatSsTermChip(row, opts = {}) {
  const includeLocation = opts.includeLocation !== false;
  const ss = formatSsCode(row?.ss_code);
  const anniversary =
    row?.ss_anniversary ||
    row?.compliance_schedule_anniversary ||
    row?.building_anniversary ||
    row?.building?.compliance_schedule_anniversary ||
    row?.building?.due_date ||
    null;
  const term = inspectionTermToken(row?.inspection_frequency, row?.due_date, anniversary);
  let inside = term?.token || 'Y-Y';
  const loc = includeLocation
    ? sanitizeLoc(row?.ss_location || row?.location || row?.unit_location || '')
    : '';
  if (loc) inside = `${inside}-${loc}`;
  return `${ss}(${inside})`;
}

/**
 * Detail / row title: Fiore 2 · ss 1.0(M-10)
 */
export function formatInspectionLabel(row, opts = {}) {
  const building =
    (row?.building_name && String(row.building_name).trim()) ||
    (row?.building?.name && String(row.building.name).trim()) ||
    'Building';
  return `${building} · ${formatSsTermChip(row, opts)}`;
}

export function formatBuildingDueLabel(row) {
  const building = (row?.building_name && String(row.building_name).trim()) || 'Building';
  const chips = [];
  if (Array.isArray(row?.ss_list) && row.ss_list.length) {
    row.ss_list.forEach((s) => {
      if (!s?.ss_code) return;
      chips.push(
        formatSsTermChip({
          ...row,
          ss_code: s.ss_code,
          due_date: s.due_date || row.due_date,
          inspection_frequency: s.inspection_frequency || row.inspection_frequency || 'ANNUAL',
          ss_location: s.location,
        })
      );
    });
  } else if (Array.isArray(row?.ss_codes)) {
    row.ss_codes.forEach((c) => chips.push(formatSsTermChip({ ...row, ss_code: c })));
  }
  const ssPart = chips.length ? chips.slice(0, 8).join(', ') + (chips.length > 8 ? '…' : '') : 'ss due';
  return `${building} — ${ssPart}`;
}
